/**
 * Client Identity Service
 * 
 * Combines browser fingerprinting and localStorage UUID to create
 * a stable client identifier for anonymous voting without user authentication.
 * 
 * This provides better vote tracking than IP-only while maintaining privacy:
 * - Browser fingerprint: Survives localStorage clearing
 * - UUID: Survives IP changes
 * - Combined: Resistant to casual vote manipulation
 */

'use client';

import { useEffect, useState } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const STORAGE_KEY = 'descout_client_uuid';

/**
 * Generate a simple UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create persistent UUID in localStorage
 */
function getOrCreateUUID(): string {
  if (typeof window === 'undefined') {
    return 'server-side-render';
  }

  try {
    let uuid = localStorage.getItem(STORAGE_KEY);
    if (!uuid) {
      uuid = generateUUID();
      localStorage.setItem(STORAGE_KEY, uuid);
    }
    return uuid;
  } catch (error) {
    // LocalStorage might be blocked (incognito, etc.)
    console.warn('[CLIENT-ID] LocalStorage unavailable, using session-only UUID');
    return generateUUID();
  }
}

/**
 * Get browser fingerprint using FingerprintJS
 */
async function getFingerprint(): Promise<string> {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  } catch (error) {
    console.error('[CLIENT-ID] Fingerprint generation failed:', error);
    // Fallback to a simple browser signature
    if (typeof window !== 'undefined') {
      const ua = window.navigator.userAgent;
      const screen = `${window.screen.width}x${window.screen.height}`;
      const tz = new Date().getTimezoneOffset();
      return `fallback-${btoa(`${ua}-${screen}-${tz}`).substring(0, 16)}`;
    }
    return 'fallback-unknown';
  }
}

/**
 * Get composite client identifier
 * Format: "fingerprint-uuid"
 */
export async function getClientId(): Promise<string> {
  const fingerprint = await getFingerprint();
  const uuid = getOrCreateUUID();
  return `${fingerprint}-${uuid}`;
}

/**
 * React hook for client identity
 * Returns stable client ID across renders
 */
export function useClientIdentity() {
  const [clientId, setClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function initializeClientId() {
      try {
        const id = await getClientId();
        if (mounted) {
          setClientId(id);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[CLIENT-ID] Initialization failed:', err);
        if (mounted) {
          setError('Failed to generate client ID');
          setIsLoading(false);
          // Set a fallback ID so voting can still work
          setClientId(`fallback-${generateUUID()}`);
        }
      }
    }

    initializeClientId();

    return () => {
      mounted = false;
    };
  }, []);

  return {
    clientId,
    isLoading,
    error,
  };
}

